import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Bounty, Submission, User } from "@/lib/types";
import { useRouter, Link } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { Separator } from "@/components/ui/separator";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
	ArrowLeft,
	ArrowUpRight,
	CheckCircle2,
	Clock,
	DollarSign,
	ExternalLink,
	GitFork,
	ThumbsUp,
	Trophy,
	User as UserIcon,
	Award,
	Send,
} from "lucide-react";

const CURRENT_USER_ID = "u1";

function timeLeft(deadline: string): string {
	const diff = new Date(deadline).getTime() - Date.now();
	if (diff <= 0) return "Deadline passed";
	const days = Math.floor(diff / (1000 * 60 * 60 * 24));
	const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
	if (days > 0) return `${days}d ${hours}h remaining`;
	return `${hours}h remaining`;
}

export function BountyDetail({ params }: { params: Record<string, string> }) {
	const { navigate } = useRouter();
	const [bounty, setBounty] = useState<Bounty | null>(null);
	const [submissions, setSubmissions] = useState<Submission[]>([]);
	const [users, setUsers] = useState<Map<string, User>>(new Map());
	const [submitOpen, setSubmitOpen] = useState(false);
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		demoUrl: "",
		repoUrl: "",
	});

	const bountyId = params.id;

	useEffect(() => {
		api.getBounty(bountyId).then(setBounty);
		api.getSubmissions(bountyId).then(setSubmissions);
		api.getUsers().then((u) => {
			const map = new Map<string, User>();
			for (const user of u) map.set(user.id, user);
			setUsers(map);
		});
	}, [bountyId]);

	async function handleVote(submissionId: string) {
		const updated = await api.voteSubmission(submissionId, CURRENT_USER_ID);
		setSubmissions((prev) =>
			prev.map((s) => (s.id === updated.id ? updated : s)).sort((a, b) => b.votes - a.votes)
		);
	}

	async function handleAward(submissionId: string) {
		const updated = await api.awardBounty(bountyId, submissionId);
		setBounty(updated);
		const subs = await api.getSubmissions(bountyId);
		setSubmissions(subs);
	}

	async function handleFork(submissionId: string) {
		await api.forkSubmission(submissionId, CURRENT_USER_ID, bountyId);
		const subs = await api.getSubmissions(bountyId);
		setSubmissions(subs);
	}

	async function handleSubmit() {
		await api.createSubmission(bountyId, {
			...formData,
			creatorId: CURRENT_USER_ID,
			isFork: false,
		});
		setFormData({ title: "", description: "", demoUrl: "", repoUrl: "" });
		setSubmitOpen(false);
		const subs = await api.getSubmissions(bountyId);
		setSubmissions(subs);
		if (bounty) setBounty({ ...bounty, submissionCount: bounty.submissionCount + 1 });
	}

	if (!bounty) {
		return (
			<div className="container mx-auto px-4 py-16 text-center text-muted-foreground">
				Loading...
			</div>
		);
	}

	const poster = users.get(bounty.posterId);
	const isPoster = bounty.posterId === CURRENT_USER_ID;
	const isOpen = bounty.status === "open";
	const isInReview = bounty.status === "in_review";

	return (
		<div className="container mx-auto px-4 py-8 max-w-5xl">
			<button
				onClick={() => navigate("/")}
				className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to bounties
			</button>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Main content */}
				<div className="lg:col-span-2 space-y-6">
					<div>
						<div className="flex items-center gap-2 mb-3">
							<StatusBadge status={bounty.status} />
							{bounty.tags.map((tag) => (
								<Badge key={tag} variant="secondary" className="text-xs">
									{tag}
								</Badge>
							))}
						</div>
						<h1 className="text-2xl font-bold tracking-tight mb-3">{bounty.title}</h1>
						<p className="text-muted-foreground leading-relaxed">{bounty.description}</p>
					</div>

					{bounty.requirements.length > 0 && (
						<div>
							<h3 className="font-semibold mb-3">Requirements</h3>
							<ul className="space-y-2">
								{bounty.requirements.map((req, i) => (
									<li key={i} className="flex items-start gap-2 text-sm">
										<CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
										<span>{req}</span>
									</li>
								))}
							</ul>
						</div>
					)}

					<Separator />

					{/* Submissions */}
					<div>
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-lg font-semibold">
								Submissions ({submissions.length})
							</h2>
							{isOpen && (
								<Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
									<DialogTrigger asChild>
										<Button size="sm" className="gap-2">
											<Send className="h-4 w-4" />
											Submit Solution
										</Button>
									</DialogTrigger>
									<DialogContent>
										<DialogHeader>
											<DialogTitle>Submit Your Solution</DialogTitle>
										</DialogHeader>
										<div className="space-y-4 pt-2">
											<div>
												<Label>Title</Label>
												<Input
													placeholder="Your solution name"
													value={formData.title}
													onChange={(e) =>
														setFormData((p) => ({ ...p, title: e.target.value }))
													}
												/>
											</div>
											<div>
												<Label>Description</Label>
												<Textarea
													placeholder="Describe your approach and key features..."
													value={formData.description}
													onChange={(e) =>
														setFormData((p) => ({
															...p,
															description: e.target.value,
														}))
													}
													rows={4}
												/>
											</div>
											<div>
												<Label>Demo URL</Label>
												<Input
													placeholder="https://..."
													value={formData.demoUrl}
													onChange={(e) =>
														setFormData((p) => ({ ...p, demoUrl: e.target.value }))
													}
												/>
											</div>
											<div>
												<Label>Repository URL</Label>
												<Input
													placeholder="https://github.com/..."
													value={formData.repoUrl}
													onChange={(e) =>
														setFormData((p) => ({ ...p, repoUrl: e.target.value }))
													}
												/>
											</div>
											<Button
												onClick={handleSubmit}
												className="w-full"
												disabled={!formData.title || !formData.description}
											>
												Submit Solution
											</Button>
										</div>
									</DialogContent>
								</Dialog>
							)}
						</div>

						{submissions.length === 0 ? (
							<Card>
								<CardContent className="py-12 text-center text-muted-foreground">
									No submissions yet. Be the first to submit a solution!
								</CardContent>
							</Card>
						) : (
							<div className="space-y-3">
								{submissions.map((sub, index) => {
									const creator = users.get(sub.creatorId);
									const hasVoted = sub.votedBy.includes(CURRENT_USER_ID);
									return (
										<Card
											key={sub.id}
											className={
												sub.isWinner
													? "border-amber-500/50 bg-amber-500/5"
													: ""
											}
										>
											<CardContent className="pt-4 pb-4">
												<div className="flex items-start gap-4">
													{/* Vote column */}
													<div className="flex flex-col items-center gap-1 pt-1">
														<button
															onClick={() => handleVote(sub.id)}
															disabled={hasVoted}
															className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
																hasVoted
																	? "bg-primary/10 text-primary"
																	: "hover:bg-secondary text-muted-foreground"
															}`}
														>
															<ThumbsUp className="h-4 w-4" />
														</button>
														<span className="text-sm font-bold">{sub.votes}</span>
													</div>

													{/* Content */}
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 mb-1">
															{sub.isWinner && (
																<Trophy className="h-4 w-4 text-amber-500" />
															)}
															{index === 0 && !sub.isWinner && submissions.length > 1 && (
																<span className="text-xs font-medium text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded">
																	Top voted
																</span>
															)}
															{sub.isFork && (
																<Badge variant="outline" className="text-xs gap-1">
																	<GitFork className="h-3 w-3" />
																	Fork
																</Badge>
															)}
														</div>
														<h4 className="font-semibold text-sm mb-1">
															{sub.title}
														</h4>
														<p className="text-xs text-muted-foreground mb-2 line-clamp-2">
															{sub.description}
														</p>
														<div className="flex items-center gap-3 text-xs text-muted-foreground">
															{creator && (
																<Link
																	to={`/profile/${creator.id}`}
																	className="flex items-center gap-1 hover:text-foreground transition-colors"
																>
																	<UserIcon className="h-3 w-3" />
																	{creator.username}
																</Link>
															)}
															{sub.demoUrl && (
																<span className="flex items-center gap-1 text-primary">
																	<ExternalLink className="h-3 w-3" />
																	Demo
																</span>
															)}
															{sub.repoUrl && (
																<span className="flex items-center gap-1 text-primary">
																	<ArrowUpRight className="h-3 w-3" />
																	Code
																</span>
															)}
														</div>
													</div>

													{/* Actions */}
													<div className="flex flex-col gap-2 shrink-0">
														{isPoster && (isOpen || isInReview) && !sub.isWinner && (
															<Button
																size="sm"
																variant="outline"
																className="gap-1 text-xs"
																onClick={() => handleAward(sub.id)}
															>
																<Award className="h-3 w-3" />
																Award
															</Button>
														)}
														{!sub.isWinner && sub.creatorId !== CURRENT_USER_ID && (
															<Button
																size="sm"
																variant="ghost"
																className="gap-1 text-xs"
																onClick={() => handleFork(sub.id)}
															>
																<GitFork className="h-3 w-3" />
																Fork
															</Button>
														)}
													</div>
												</div>
											</CardContent>
										</Card>
									);
								})}
							</div>
						)}
					</div>
				</div>

				{/* Sidebar */}
				<div className="space-y-4">
					<Card>
						<CardContent className="pt-5 pb-5 space-y-4">
							<div className="text-center">
								<div className="text-3xl font-bold text-emerald-600 mb-1">
									${bounty.bountyAmount}
								</div>
								<p className="text-xs text-muted-foreground">Bounty Prize</p>
							</div>
							<Separator />
							<div className="space-y-3 text-sm">
								<div className="flex items-center justify-between">
									<span className="text-muted-foreground flex items-center gap-2">
										<Clock className="h-4 w-4" />
										Deadline
									</span>
									<span className="font-medium">{timeLeft(bounty.deadline)}</span>
								</div>
								<div className="flex items-center justify-between">
									<span className="text-muted-foreground flex items-center gap-2">
										<DollarSign className="h-4 w-4" />
										Status
									</span>
									<StatusBadge status={bounty.status} />
								</div>
								<div className="flex items-center justify-between">
									<span className="text-muted-foreground flex items-center gap-2">
										<Send className="h-4 w-4" />
										Submissions
									</span>
									<span className="font-medium">{bounty.submissionCount}</span>
								</div>
							</div>
						</CardContent>
					</Card>

					{poster && (
						<Card>
							<CardContent className="pt-5 pb-5">
								<p className="text-xs text-muted-foreground mb-3">Posted by</p>
								<Link
									to={`/profile/${poster.id}`}
									className="flex items-center gap-3 hover:opacity-80 transition-opacity"
								>
									<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
										{poster.avatar}
									</div>
									<div>
										<p className="font-semibold text-sm">{poster.username}</p>
										<p className="text-xs text-muted-foreground">
											{poster.bountiesPosted} bounties posted
										</p>
									</div>
								</Link>
							</CardContent>
						</Card>
					)}

					{bounty.winnerId && (
						<Card className="border-amber-500/50 bg-amber-500/5">
							<CardContent className="pt-5 pb-5">
								<div className="flex items-center gap-2 mb-3">
									<Trophy className="h-4 w-4 text-amber-500" />
									<p className="text-xs font-semibold text-amber-600">Winner</p>
								</div>
								{(() => {
									const winner = users.get(bounty.winnerId!);
									if (!winner) return null;
									return (
										<Link
											to={`/profile/${winner.id}`}
											className="flex items-center gap-3 hover:opacity-80 transition-opacity"
										>
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-white text-sm font-bold">
												{winner.avatar}
											</div>
											<div>
												<p className="font-semibold text-sm">{winner.username}</p>
												<p className="text-xs text-muted-foreground">
													{winner.wins} wins | ${winner.totalEarnings} earned
												</p>
											</div>
										</Link>
									);
								})()}
							</CardContent>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
}
